import { useState, useEffect, useRef } from 'react';
import { formatFranc } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import BackButton from '../components/BackButton';
import { User, Trophy, Calendar, Shield, Lock, AlertTriangle, Camera, Crown, Trash2, Loader2 } from 'lucide-react';
import LinkedAccountsPanel from '../components/LinkedAccountsPanel';
import { ChangePasswordCard } from '../components/ChangePasswordCard';
import { ChangeEmailCard } from '../components/ChangeEmailCard';
import { DeleteAccountCard } from '../components/DeleteAccountCard';
import { UsernamePickerModal } from '../components/UsernamePickerModal';
import SeasonHistory from '../components/SeasonHistory';

const DICEBEAR_BASE = 'https://api.dicebear.com/7.x';

// Avatar options: style + seed variations
const AVATAR_OPTIONS = [
  { style: 'personas', label: 'Male 1', seed: 'male1' },
  { style: 'personas', label: 'Male 2', seed: 'male2' },
  { style: 'personas', label: 'Male 3', seed: 'male3' },
  { style: 'avataaars', label: 'Male 4', seed: 'guy1' },
  { style: 'avataaars', label: 'Male 5', seed: 'guy2' },
  { style: 'personas', label: 'Female 1', seed: 'female1' },
  { style: 'personas', label: 'Female 2', seed: 'female2' },
  { style: 'personas', label: 'Female 3', seed: 'woman3' },
  { style: 'avataaars', label: 'Female 4', seed: 'lady1' },
  { style: 'avataaars', label: 'Female 5', seed: 'lady2' },
  { style: 'bottts', label: 'Robot 1', seed: 'bot1' },
  { style: 'bottts', label: 'Robot 2', seed: 'bot2' },
  { style: 'fun-emoji', label: 'Emoji 1', seed: 'happy' },
  { style: 'fun-emoji', label: 'Emoji 2', seed: 'cool' },
  { style: 'pixel-art', label: 'Pixel 1', seed: 'hero' },
  { style: 'pixel-art', label: 'Pixel 2', seed: 'star' },
];

function getAvatarUrl(style, seed, size = 128) {
  return `${DICEBEAR_BASE}/${style}/svg?seed=${encodeURIComponent(seed)}&size=${size}&backgroundColor=transparent`;
}

// Resolve a stored avatar_url: dicebear/photo URLs may be absolute (dicebear)
// or relative (`/api/static/profile_photos/...`) — prefix the backend origin
// for the relative ones so the <img> resolves on every host.
// AUDIT 2026-06 (P2): require relative paths start with '/' (defence-in-depth
// against a malformed server value), and append a cache-buster for uploaded
// photos so a re-upload to the same stable path is not served from cache.
function resolveAvatarUrl(u, cacheBust) {
  if (!u) return null;
  if (u.startsWith('http')) return u;
  if (!u.startsWith('/')) return null;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  if (!cacheBust) return `${base}${u}`;
  const sep = u.includes('?') ? '&' : '?';
  return `${base}${u}${sep}v=${cacheBust}`;
}

export default function ProfilePage({ onBack, focusEmail = false }) {
  const { user, logout, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  // v6.1.17.7 N.2 — Optional username picker (allows dismiss on this page).
  const [showUsernamePicker, setShowUsernamePicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);  // bump to cache-bust the <img> after upload/remove
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get('/stats/my')
      .then(res => { setStats(res.data); setLoading(false); })
      .catch(() => { setLoading(false); toast.error('Could not load your profile stats. Please try again.'); });
  }, []);

  const isPremium = !!(user?.premium || stats?.premium);

  // Persist a DiceBear avatar choice server-side (any user).
  const selectAvatar = async (avatar) => {
    try {
      await api.put('/profile/avatar', { style: avatar.style, seed: avatar.seed });
      await refreshUser();
      setShowAvatarPicker(false);
      toast.success('Avatar updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update avatar');
    }
  };

  // Premium custom photo upload (NSFW-moderated server-side, 3/day cap).
  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';  // allow re-selecting the same file
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5 MB).'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      await refreshUser();
      setPhotoVersion(v => v + 1);
      setShowAvatarPicker(false);
      toast.success('Photo updated \u2713');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) toast.error(detail || 'Custom photos are a Premium perk.');
      else if (status === 422) toast.error(detail || 'That image didn\u2019t pass our content check.');
      else if (status === 429) toast.error(detail || 'Daily upload limit reached. Try again tomorrow.');
      else toast.error(detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      await api.delete('/profile/photo');
      await refreshUser();
      setPhotoVersion(v => v + 1);
      toast.success('Photo removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove photo');
    }
  };

  if (loading) return <div className="p-4"><Skeleton className="h-60 w-full rounded-2xl" /></div>;

  const avatarUrl = resolveAvatarUrl(user?.avatar_url, user?.avatar_type === 'photo' ? photoVersion : null);
  const hasPhoto = user?.avatar_type === 'photo';

  return (
    <div className="p-4 pb-24" data-testid="profile-page">
      {onBack && <BackButton onClick={onBack} />}

      {/* Profile Header */}
      <div className="bg-card border border-border/60 rounded-2xl p-6 mb-4 text-center relative overflow-hidden">
        <div className="spotlight-overlay absolute inset-0" />
        <div className="relative z-10">
          {/* Avatar — clickable to change */}
          <button
            className="relative h-20 w-20 rounded-full mx-auto mb-3 border-2 border-primary/30 overflow-hidden bg-primary/10 flex items-center justify-center group"
            onClick={() => setShowAvatarPicker(!showAvatarPicker)}
            data-testid="avatar-change-btn"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
            ) : (
              <User className="h-10 w-10 text-primary" />
            )}
            {/* "Change" overlay */}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
              <span className="text-white text-xs font-semibold">Change</span>
            </div>
            {/* Always show "Change" label */}
            <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[8px] font-bold px-2 py-0.5 rounded-full">
              Change
            </div>
          </button>

          {/* Avatar Picker */}
          {showAvatarPicker && (
            <div className="mb-4 bg-secondary/50 rounded-xl p-3" data-testid="avatar-picker">
              {/* Premium custom photo upload */}
              {isPremium ? (
                <div className="mb-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handlePhotoSelected}
                    data-testid="photo-upload-input"
                  />
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="photo-upload-btn"
                  >
                    {uploading
                      ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Checking photo...</>
                      : <><Camera className="h-3.5 w-3.5 mr-1.5" /> Upload a photo</>}
                  </Button>
                  {hasPhoto && (
                    <button
                      onClick={handleRemovePhoto}
                      className="text-[11px] text-muted-foreground hover:text-destructive mt-2 inline-flex items-center gap-1 mx-auto"
                      data-testid="photo-remove-btn"
                    >
                      <Trash2 className="h-3 w-3" /> Remove photo
                    </button>
                  )}
                  <p className="text-[9px] text-muted-foreground mt-1.5">
                    Photos are auto-checked for safety. Max 3 uploads/day.
                  </p>
                </div>
              ) : (
                <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 p-2.5 flex items-center gap-2" data-testid="photo-premium-cta">
                  <Crown className="h-4 w-4 text-primary shrink-0" />
                  <p className="text-[11px] text-muted-foreground flex-1">
                    Upload your own photo with <span className="font-semibold text-foreground">Premium</span>.
                  </p>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => { if (onBack) onBack(); window.location.hash = '#premium'; }}
                    data-testid="photo-upgrade-btn">
                    Upgrade
                  </Button>
                </div>
              )}

              <p className="text-xs font-semibold text-muted-foreground mb-2">Or choose an avatar</p>
              <div className="grid grid-cols-4 gap-2">
                {AVATAR_OPTIONS.map((av, i) => (
                  <button
                    key={i}
                    onClick={() => selectAvatar(av)}
                    className={`h-14 w-14 rounded-full mx-auto overflow-hidden border-2 transition-all ${
                      !hasPhoto && user?.avatar_seed === av.seed && user?.avatar_style === av.style
                        ? 'border-primary scale-110' : 'border-transparent hover:border-border'
                    }`}
                    data-testid={`avatar-option-${av.seed}`}
                  >
                    <img src={getAvatarUrl(av.style, av.seed, 64)} alt={av.label} className="h-full w-full" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
            {user?.username || 'Player'}
          </h1>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Badge variant={stats?.premium ? 'default' : 'secondary'}>
              {stats?.premium ? 'Premium' : 'Free Tier'}
            </Badge>
            <Badge variant="secondary" className="text-[10px]">
              <Calendar className="h-3 w-3 mr-1" />
              Since {stats?.member_since ? new Date(stats.member_since).toLocaleDateString() : 'N/A'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-card border border-border/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="h-4 w-4 text-[hsl(var(--accent))]" />
            <p className="text-xs text-muted-foreground">Total ₣ Earned (all leagues)</p>
          </div>
          <p className="text-2xl font-bold tabular-nums">{formatFranc(stats?.total_points)}</p>
        </div>
        <div className="bg-card border border-border/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">Teams</p>
          </div>
          <p className="text-2xl font-bold tabular-nums">{stats?.teams_count || 0}</p>
        </div>
      </div>

      {/* League Rankings */}
      {stats?.league_ranks?.length > 0 && (
        <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Shield className="h-4 w-4" /> My Leagues</h3>
          {stats.league_ranks.map((lr, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-secondary/20 mb-1">
              <div>
                <p className="text-sm font-medium">{lr.league_name}</p>
                <Badge variant="secondary" className="text-[9px]">{lr.league_type}</Badge>
              </div>
              <p className="text-lg font-bold tabular-nums text-[hsl(var(--accent-text))]" data-testid={`profile-league-rank-${i}`}>#{lr.rank || '?'}</p>
            </div>
          ))}
        </div>
      )}

      {/* v6.1.17.7 N.2 — Legacy banner: prompt the user to pick a new
          username when their existing one violates the new spec. */}
      {user?.username_legacy && (
        <div
          className="rounded-2xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-4 mb-4 flex items-start gap-3"
          data-testid="username-legacy-banner"
        >
          <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
              Your username doesn&apos;t match our new format.
            </p>
            <p className="text-xs text-amber-800 dark:text-amber-200/80 mt-1">
              Pick a new one before placing your next pick, swap, or chat message.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 border-amber-400 dark:border-amber-700"
              onClick={() => setShowUsernamePicker(true)}
              data-testid="username-legacy-update-btn"
            >
              Update username
            </Button>
          </div>
        </div>
      )}

      {/* 2026-11-01 (owner) — every finished quarter's final rank + points. */}
      <SeasonHistory path="/recaps/history/me" showEmpty />

      {/* Account */}
      <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4">
        <h3 className="text-sm font-semibold mb-3">Account</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center" data-testid="profile-username-row">
            <span className="text-muted-foreground">Username</span>
            <span className="font-medium inline-flex items-center gap-1.5">
              {stats?.username}
              <Lock
                className="h-3 w-3 text-muted-foreground"
                aria-label="Username is permanent"
              />
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground -mt-1 text-right">
            Frozen after first set to keep public identifiers stable.
          </p>
          <div className="flex justify-between"><span className="text-muted-foreground">Status</span>
            <Badge variant={stats?.premium ? 'default' : 'secondary'}>{stats?.premium ? 'Premium' : 'Free'}</Badge>
          </div>
        </div>
      </div>

      {/* v6.1.17.7 N.2 — Linked accounts panel (Email · Google · Apple). */}
      <LinkedAccountsPanel />

      {/* 2026-09-17 — change email (password accounts); auto-opens from the bounce card. */}
      <ChangeEmailCard autoOpen={focusEmail} />

      {/* Authenticated change-password (email accounts). */}
      <ChangePasswordCard />

      {/* Danger zone — self-service account deletion (store compliance). */}
      <DeleteAccountCard />

      {/* v6.1.17.7 N.2 — Optional username picker modal (dismissible
          when launched from this page, no-dismiss when launched by the
          global 403 interceptor in src/lib/api.js). */}
      <UsernamePickerModal
        open={showUsernamePicker}
        onClose={(saved) => {
          setShowUsernamePicker(false);
          // If saved, the user object on the next page hydrate will
          // have username_legacy: false, so the banner disappears.
          if (saved && typeof window !== 'undefined') window.location.reload();
        }}
        reason={{ username_legacy: !!user?.username_legacy }}
        allowDismiss={true}
      />

      <Button variant="destructive" className="w-full" onClick={logout} data-testid="profile-signout">
        Sign Out
      </Button>
    </div>
  );
}
